import { API_ENDPOINTS } from '@/config/apiEndpoints';
import { apiClient } from '@/shared/utils/api/apiClient';
import { parseOrThrow } from '@/shared/utils/api/parseResponse';
import type { Paginated } from '@/shared/types/common.types';
import {
  auditTrailSchema,
  decisionRequest,
  featureStatusSchema,
  memberModelListSchema,
  mergeRequestPageSchema,
  mergeRequestSchema,
  toAuditEntry,
  toMergeRequest,
  toMergeRequestModel,
  toMergeRequestSummary,
} from '../api/mergeRequestContracts';
import {
  diffQueryString,
  mergeRequestDiffSchema,
  modelBaselinesSchema,
  participantListSchema,
  productionRequestBody,
  revertBody,
  toMergeRequestDiff,
  toModelBaselines,
  toParticipants,
} from '../api/baselineContracts';
import type {
  DiffQuery,
  MergeRequestDiff,
  ModelBaselines,
  Participant,
  ProductionRequestInput,
  RevertInput,
} from '../types/baseline.types';
import type {
  DecisionInput,
  MergeRequest,
  MergeRequestAuditEntry,
  MergeRequestModel,
  MergeRequestQuery,
  MergeRequestSummary,
} from '../types/mergeRequest.types';

/**
 * Merge Model Request calls. Core owns every rule (status machine, roles, no self-approval,
 * concurrency); this service only translates. Core answers 404 both when the feature is off
 * and when the caller has no access to the model, so a 404 is never read as "does not exist".
 */
class MergeRequestService {
  /** Whether the feature is switched on in Core. Every other route answers 404 while it is off. */
  async isEnabled(): Promise<boolean> {
    const payload = await apiClient.get<unknown>(API_ENDPOINTS.mergeRequests.feature);
    return parseOrThrow(featureStatusSchema, payload, 'merge request feature status').enabled;
  }

  /** The models the caller is a member of (Core caps the list at 200). */
  async listModels(): Promise<readonly MergeRequestModel[]> {
    const payload = await apiClient.get<unknown>(API_ENDPOINTS.mergeRequests.models);
    return parseOrThrow(memberModelListSchema, payload, 'merge request models').map(toMergeRequestModel);
  }

  async list(query: MergeRequestQuery): Promise<Paginated<MergeRequestSummary>> {
    const params = new URLSearchParams({
      modelId: query.modelId,
      pageIndex: String(query.page),
      pageSize: String(query.pageSize),
    });
    if (query.status !== undefined) params.set('status', query.status);

    const payload = await apiClient.get<unknown>(`${API_ENDPOINTS.mergeRequests.list}?${params.toString()}`);
    const page = parseOrThrow(mergeRequestPageSchema, payload, 'merge request list');
    return {
      items: page.items.map(toMergeRequestSummary),
      totalCount: page.totalCount,
      page: page.pageIndex,
      pageSize: page.pageSize,
    };
  }

  async getById(id: string): Promise<MergeRequest> {
    const payload = await apiClient.get<unknown>(API_ENDPOINTS.mergeRequests.detail(id));
    return toMergeRequest(parseOrThrow(mergeRequestSchema, payload, 'merge request'));
  }

  async auditTrail(id: string): Promise<readonly MergeRequestAuditEntry[]> {
    const payload = await apiClient.get<unknown>(API_ENDPOINTS.mergeRequests.auditTrail(id));
    return parseOrThrow(auditTrailSchema, payload, 'merge request audit trail').map(toAuditEntry);
  }

  async addComment(id: string, text: string): Promise<MergeRequest> {
    const payload = await apiClient.post<unknown>(API_ENDPOINTS.mergeRequests.comments(id), { text });
    return toMergeRequest(parseOrThrow(mergeRequestSchema, payload, 'merge request'));
  }

  async withdraw(id: string, reason?: string): Promise<MergeRequest> {
    const trimmed = reason?.trim();
    const payload = await apiClient.post<unknown>(
      API_ENDPOINTS.mergeRequests.withdraw(id),
      trimmed === undefined || trimmed.length === 0 ? {} : { reason: trimmed }
    );
    return toMergeRequest(parseOrThrow(mergeRequestSchema, payload, 'merge request'));
  }

  async reviewDecision(id: string, input: DecisionInput): Promise<MergeRequest> {
    const payload = await apiClient.post<unknown>(
      API_ENDPOINTS.mergeRequests.reviewDecision(id),
      decisionRequest(input)
    );
    return toMergeRequest(parseOrThrow(mergeRequestSchema, payload, 'merge request'));
  }

  async authorizerDecision(id: string, input: DecisionInput): Promise<MergeRequest> {
    const payload = await apiClient.post<unknown>(
      API_ENDPOINTS.mergeRequests.authorizerDecision(id),
      decisionRequest(input)
    );
    return toMergeRequest(parseOrThrow(mergeRequestSchema, payload, 'merge request'));
  }

  /** Current SHAR and PROD of a model plus its baseline history. */
  async baselines(modelId: string): Promise<ModelBaselines> {
    const payload = await apiClient.get<unknown>(API_ENDPOINTS.mergeRequests.baselines(modelId));
    return toModelBaselines(parseOrThrow(modelBaselinesSchema, payload, 'model baselines'));
  }

  /** Restores an earlier baseline as the current one. Adds a history row; nothing is deleted. */
  async revert(modelId: string, input: RevertInput): Promise<ModelBaselines> {
    const payload = await apiClient.post<unknown>(
      API_ENDPOINTS.mergeRequests.revert(modelId, input.stage === 'Shared' ? 'shared' : 'published'),
      revertBody(input)
    );
    return toModelBaselines(parseOrThrow(modelBaselinesSchema, payload, 'model baselines'));
  }

  async participants(modelId: string): Promise<readonly Participant[]> {
    const payload = await apiClient.get<unknown>(API_ENDPOINTS.mergeRequests.participants(modelId));
    return toParticipants(parseOrThrow(participantListSchema, payload, 'merge request participants'));
  }

  async diff(id: string, query: DiffQuery): Promise<MergeRequestDiff> {
    const payload = await apiClient.get<unknown>(
      `${API_ENDPOINTS.mergeRequests.diff(id)}?${diffQueryString(query)}`
    );
    return toMergeRequestDiff(parseOrThrow(mergeRequestDiffSchema, payload, 'merge request diff'));
  }

  async retryDiff(id: string): Promise<void> {
    await apiClient.post<unknown>(API_ENDPOINTS.mergeRequests.diffRetry(id), {});
  }

  async retryFinalize(id: string): Promise<MergeRequest> {
    const payload = await apiClient.post<unknown>(API_ENDPOINTS.mergeRequests.finalizeRetry(id), {});
    return toMergeRequest(parseOrThrow(mergeRequestSchema, payload, 'merge request'));
  }

  /** Promotes the current SHAR to PROD. No file is uploaded: Core uses the baseline's file. */
  async createProductionRequest(input: ProductionRequestInput): Promise<MergeRequest> {
    const payload = await apiClient.post<unknown>(API_ENDPOINTS.mergeRequests.list, productionRequestBody(input));
    return toMergeRequest(parseOrThrow(mergeRequestSchema, payload, 'merge request'));
  }

  /** The archived `.rvt` as a blob. Core writes an access-log entry before streaming. */
  downloadFile(modelId: string, sha256: string): Promise<Blob> {
    return apiClient.getBlob(API_ENDPOINTS.mergeRequests.archivedFile(modelId, sha256));
  }
}

export const mergeRequestService = new MergeRequestService();
